// Widths of the frame's columns the person drags (R-U17, um/2026-10-31/01 п.1, п.4): the left column, the second column, the column of
// property labels, the list of the Gantt. Each is one width for the whole app, kept in this browser; the columns read it as a CSS
// variable on the document, so every section takes it at once. One drag edge for all of them (DESIGN.md «Тянущаяся граница»).

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { cx } from './primitives.tsx';
import s from './col-width.module.css';

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;
const browserStorage = (): Storage | undefined => { try { return globalThis.localStorage; } catch { return undefined; } };

export interface ColWidth {
  readonly min: number; readonly max: number;
  /** The dragged width, or null — the standard one of the tokens. */
  get(): number | null;
  /** The width now: the dragged one, or the standard one (read from the CSS variable, or `fallback`). */
  current(): number;
  /** Clamped to the limits; null returns the standard width. */
  set(w: number | null): void;
  subscribe(cb: () => void): () => void;
}

export function colWidth({ key, cssVar, min, max, fallback, storage = browserStorage() }: {
  key: string; min: number; max: number;
  /** The variable the columns are drawn with (`--side-w`); without it the width is read by the screen itself (the Gantt). */
  cssVar?: string;
  /** The standard width when no CSS variable gives it. */
  fallback: number;
  storage?: Storage;
}): ColWidth {
  const clamp = (w: number) => Math.round(Math.min(max, Math.max(min, w)));
  const listeners = new Set<() => void>();
  let width: number | null = (() => { try { const v = Number(storage?.getItem(key)); return v ? clamp(v) : null; } catch { return null; } })();
  const root = () => (typeof document === 'undefined' ? undefined : document.documentElement);
  const apply = () => {
    const el = root();
    if (!el || !cssVar) return;
    if (width === null) el.style.removeProperty(cssVar); else el.style.setProperty(cssVar, `${width}px`);
  };
  apply();
  return {
    min, max,
    get: () => width,
    current: () => {
      if (width !== null) return width;
      const el = root();
      const std = el && cssVar ? parseFloat(getComputedStyle(el).getPropertyValue(cssVar)) : NaN;
      return Number.isFinite(std) ? std : fallback;
    },
    set(w) {
      width = w === null ? null : clamp(w);
      try { if (width === null) storage?.removeItem(key); else storage?.setItem(key, String(width)); } catch { /* convenience only */ }
      apply();
      for (const l of listeners) l();
    },
    subscribe(cb) { listeners.add(cb); return () => { listeners.delete(cb); }; },
  };
}

// The three widths of the frame: one place for their keys and limits.
export const SIDE_W = colWidth({ key: 'confdeck:side-w', cssVar: '--side-w', min: 200, max: 400, fallback: 248 });
export const LIST_W = colWidth({ key: 'confdeck:list-w', cssVar: '--list-w', min: 240, max: 480, fallback: 300 });
export const LABEL_W = colWidth({ key: 'confdeck:label-w', cssVar: '--label-w', min: 100, max: 320, fallback: 170 });

/** The dragged width or the standard one, re-rendered on every change. */
export const useColWidth = (w: ColWidth) => useSyncExternalStore(w.subscribe, w.current);

/**
 * One drag of an edge: the width follows the pointer from `x0` until the button is let go — also outside the window, where no
 * `pointerup` comes: a lost pointer capture, a cancelled gesture or the window losing focus end it too, so the page never stays in
 * the dragging mode (`data-col-resizing`). Returns the function that ends it (the edge calls it when it unmounts).
 */
export function startColDrag(width: ColWidth, x0: number, { win, root, edge }: {
  win: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
  root: { setAttribute(n: string, v: string): void; removeAttribute(n: string): void };
  /** The edge holding the pointer capture: its `lostpointercapture` ends the drag. */
  edge?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
}): () => void {
  const w0 = width.current();
  const move = (ev: Event) => width.set(w0 + (ev as PointerEvent).clientX - x0);
  let done = false;
  const stop = () => {
    if (done) return;
    done = true;
    win.removeEventListener('pointermove', move);
    for (const t of ['pointerup', 'pointercancel', 'blur'] as const) win.removeEventListener(t, stop);
    edge?.removeEventListener('lostpointercapture', stop);
    root.removeAttribute('data-col-resizing');
  };
  root.setAttribute('data-col-resizing', '');
  win.addEventListener('pointermove', move);
  for (const t of ['pointerup', 'pointercancel', 'blur'] as const) win.addEventListener(t, stop);
  edge?.addEventListener('lostpointercapture', stop);
  return stop;
}

/**
 * The drag edge of a column: `col-resize`, a thin line under the pointer, a double click returns the standard width, arrows step it.
 * It sits at the right edge of its positioned parent; `className` places it elsewhere (between the label and the value). `hidden` —
 * a second edge of the same width on one screen (the Gantt's list): it drags, but the keyboard and the reader know only the first.
 */
export function ColResizer({ width, label, className, hidden }: { width: ColWidth; label: string; className?: string; hidden?: boolean }) {
  const now = useColWidth(width);
  const stop = useRef<(() => void) | null>(null);
  useEffect(() => () => stop.current?.(), []);
  const start = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    stop.current?.();
    const edge = e.currentTarget;
    try { edge.setPointerCapture(e.pointerId); } catch { /* an old browser: the window's events are enough */ }
    stop.current = startColDrag(width, e.clientX, { win: window, root: document.documentElement, edge });
  };
  const key = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); width.set(width.current() + (e.key === 'ArrowLeft' ? -16 : 16)); }
  };
  return <div className={cx(s.edge, className)} role={hidden ? undefined : 'separator'} aria-hidden={hidden || undefined} aria-orientation={hidden ? undefined : 'vertical'}
    aria-label={hidden ? undefined : label} aria-valuemin={hidden ? undefined : width.min} aria-valuemax={hidden ? undefined : width.max} aria-valuenow={hidden ? undefined : Math.round(now)}
    tabIndex={hidden ? -1 : 0} title={`${label}: потяните, двойной щелчок — по умолчанию`} onPointerDown={start} onDoubleClick={() => width.set(null)} onKeyDown={hidden ? undefined : key}
    onClick={(e) => e.stopPropagation()} />;
}
